<script setup>
import { socialShareImage, usePageHead } from '@composables/use-page-head'
import { useShowsStore } from '@stores/shows'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import DataLayer from '@services/data-layer/index'
import ShowService from './../../services/show-service'
import Overview from '@components/show/overview'
import About from '@components/show/about'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes name the tab, so the name is mapped back here.
const MAIN_COMPONENTS = { Overview, About }

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'Overview',
    },
})

const route = useRoute()
const showsStore = useShowsStore()

const latestMixes = ref([])
const popularMixes = ref([])
const artists = ref([])

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const show = computed(() => showsStore.activeObject)

const tabs = computed(() => {
    const id = route.params.id
    return [
        { to: { name: 'show', params: { id } }, label: 'Overview' },
        { to: { name: 'show/about', params: { id } }, label: 'About' },
    ]
})

function checkForAdditionalReach(id) {
    if (ShowService().hasAdditionalReach(id)) showsStore.increaseReach()
}

onMounted(() => {
    const id = route.params.id

    Promise.all([
        DataLayer.shows.getById(id),
        DataLayer.mixes.getPopularByShow(id),
        DataLayer.mixes.getLatestByShow(id),
    ]).then(([showResponse, popular, latest]) => {
        showsStore.setActive(showResponse.body)
        artists.value = [showResponse.body.artist]
        popularMixes.value = popular.body
        latestMixes.value = latest.body
        checkForAdditionalReach(id)
    })
})

usePageHead(computed(() => {
    const title = show.value?.title || 'Show'
    const genre = show.value?.genre?.length ? show.value.genre[0] : ''

    return {
        title,
        description: show.value?.summary
            || `Listen to ${title} on FRISKY${genre ? ' • ' + genre : ''}`,
        image: show.value?.album_art?.url || socialShareImage(),
        type: 'music.song',
    }
}))
</script>

<template>
    <BaseHero
        :resource="show"
        :mix="latestMixes[0]"
        resource-type="show"
    />
    <BaseTabPanel
        :tabs="tabs"
        class="tabs"
        is-unpadded
        address-navigation
    />
    <BaseContent>
        <component
            :is="mainView"
            :show="show"
            :artists="artists"
            :popular-mixes="popularMixes"
            :latest-mixes="latestMixes"
        />
    </BaseContent>
</template>

<style lang="scss" scoped>
@import '@design';

.tabs {
    @include tablet {
        padding-bottom: $s-xxl;
    }
}
</style>
